import React, { useState } from 'react';
import axios from 'axios';
import { Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import OwnerAccount from './management/OwnerAccount';
import BackupTools from './management/BackupTools';

export default function OwnerBackupPanel({ user }) {
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState('');
  if (user?.role !== 'super_admin') return null;

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    setResult('');
    try {
      const response = await axios.get('/backups/full', { responseType: 'blob', timeout: 0 });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      link.href = url;
      link.download = `EduCore-backup-${stamp}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      const missing = Number(response.headers['x-backup-missing-files'] || 0);
      const message = missing
        ? `Respaldo descargado. No se pudieron recuperar ${missing} archivo(s) adjunto(s). Consulta el detalle en manifest.json.`
        : 'Respaldo descargado. Revisa el resumen por escuela en manifest.json.';
      setResult(message);
      if (missing) toast.warning(message);
      else toast.success('Respaldo descargado');
    } catch (error) {
      let message = 'No se pudo descargar el respaldo. Intenta nuevamente.';
      try {
        const data = error.response?.data;
        const detail = data instanceof Blob ? JSON.parse(await data.text()).detail : data?.detail;
        if (typeof detail === 'string') message = detail;
      } catch (_) { /* Keep the readable fallback. */ }
      setResult(message);
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="owner-profile">
      <OwnerAccount />
      <Card>
        <CardHeader><CardTitle>Respaldo de todas las escuelas</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Descarga un ZIP con los datos de todas las escuelas en JSON, un resumen por escuela
            y los archivos adjuntos disponibles. Incluye estudiantes, usuarios, notas, asistencia,
            pagos, facturas y las demás colecciones de esta aplicación.
          </p>
          <p className="text-sm text-slate-600">Contiene información confidencial. Guarda el respaldo en un lugar privado.</p>
          <Button onClick={download} disabled={downloading} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="download-full-backup">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {downloading ? 'Preparando respaldo…' : 'Descargar respaldo completo'}
          </Button>
          <p className="text-sm text-slate-500" role="status" aria-live="polite">
            {downloading ? 'La preparación puede tardar varios minutos. Mantén esta página abierta.' : result}
          </p>
        </CardContent>
      </Card>
      <BackupTools />
    </div>
  );
}
